import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw, BarChart3, Calendar, X } from 'lucide-react';
import { vouchersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';

type VoucherType = { id: number; name: string; count: number };
type AccountRow = { label: string; count: number };
type MonthRow = {
  label: string; year: number; month: number; count: number; dateFrom: string; dateTo: string;
};

export default function Statistics() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [voucherTypes, setVoucherTypes] = useState<VoucherType[]>([]);
  const [voucherTotal, setVoucherTotal] = useState(0);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selectedVoucherType, setSelectedVoucherType] = useState<VoucherType | null>(null);
  const [months, setMonths] = useState<MonthRow[]>([]);
  const [financialYear, setFinancialYear] = useState<number | null>(null);
  const dateFrom = searchParams.get('from') || '';
  const dateTo = searchParams.get('to') || '';
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getStatistics({ date_from: dateFrom || undefined, date_to: dateTo || undefined });
      if (res.success) {
        setVoucherTypes(res.data.voucherTypes || []);
        setVoucherTotal(res.data.voucherTotal || 0);
        setAccounts(res.data.accounts || []);
      }
    } catch {
      showError('Error', 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, showError]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setDraftFrom(dateFrom); }, [dateFrom]);
  useEffect(() => { setDraftTo(dateTo); }, [dateTo]);

  const applyDateFilter = () => {
    if (draftFrom && draftTo && draftFrom > draftTo) {
      showError('Invalid date range', 'From date cannot be after To date');
      return;
    }
    const next: Record<string, string> = {};
    if (draftFrom) next.from = draftFrom;
    if (draftTo) next.to = draftTo;
    setSearchParams(next, { replace: true });
  };

  const clearDateFilter = () => {
    setDraftFrom('');
    setDraftTo('');
    setSearchParams({}, { replace: true });
  };

  const dateFilterDirty = draftFrom !== dateFrom || draftTo !== dateTo;
  const hasDateFilter = !!(dateFrom || dateTo);

  const openVoucherType = async (voucherType: VoucherType) => {
    setSelectedVoucherType(voucherType);
    setDetailLoading(true);
    try {
      const res = await vouchersApi.getStatisticsMonthly(voucherType.id);
      if (res.success) {
        setMonths(res.data.months || []);
        setFinancialYear(res.data.financialYear || null);
      }
    } catch {
      setSelectedVoucherType(null);
      showError('Error', 'Failed to load month-wise voucher counts');
    } finally {
      setDetailLoading(false);
    }
  };

  const openMonth = (month: MonthRow) => {
    if (!selectedVoucherType) return;
    const query = new URLSearchParams({
      from: month.dateFrom,
      to: month.dateTo,
      typeId: String(selectedVoucherType.id),
      typeName: selectedVoucherType.name,
    });
    navigate(`/billing/daybook?${query.toString()}`);
  };

  const goBack = () => {
    if (selectedVoucherType) {
      setSelectedVoucherType(null);
      setMonths([]);
      setFinancialYear(null);
    } else {
      navigate(-1);
    }
  };

  const refresh = () => {
    if (selectedVoucherType) openVoucherType(selectedVoucherType);
    else load();
  };

  const monthTotal = months.reduce((sum, month) => sum + Number(month.count || 0), 0);

  return (
    <div className="flex flex-col w-full fixed left-0 right-0 top-14 bottom-16 sm:static sm:h-full sm:top-auto sm:bottom-auto" style={{ overscrollBehavior: 'contain' }}>
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-1 min-w-0">
          <button onClick={goBack} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 -ml-1" title="Back">
            <ChevronLeft size={20} />
          </button>
          <BarChart3 size={18} className="text-blue-600 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-[17px] font-bold text-slate-800">Statistics</h1>
            {selectedVoucherType && (
              <p className="text-[11px] text-blue-700 font-medium truncate">
                Types of Vouchers / {selectedVoucherType.name} / Month-wise
              </p>
            )}
          </div>
        </div>
        <button onClick={refresh} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh">
          <RefreshCw size={16} className={(loading || detailLoading) ? 'animate-spin' : ''} />
        </button>
      </div>

      {!selectedVoucherType && (
        <div className="flex-none bg-slate-50 border-b border-slate-200 px-3 py-2 print:hidden">
          <div className="max-w-5xl mx-auto flex items-center gap-2 flex-wrap">
            <div className="flex items-stretch border border-slate-300 rounded bg-white overflow-hidden flex-1 min-w-[220px] max-w-xl">
              <div className="flex items-center gap-1 px-2 border-r border-slate-300 bg-slate-100">
                <Calendar size={12} className="text-slate-400" />
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">From</span>
              </div>
              <input type="date" value={draftFrom} onChange={e => setDraftFrom(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') applyDateFilter(); }}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 flex-1 min-w-0" />
              <div className="flex items-center px-2 border-l border-r border-slate-300 bg-slate-100">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">To</span>
              </div>
              <input type="date" value={draftTo} onChange={e => setDraftTo(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') applyDateFilter(); }}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 flex-1 min-w-0" />
            </div>
            <button onClick={applyDateFilter} disabled={!dateFilterDirty}
              className={`text-[13px] px-3 py-1.5 rounded text-white transition-colors ${dateFilterDirty ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-300 cursor-not-allowed'}`}>
              Apply
            </button>
            {hasDateFilter && (
              <button onClick={clearDateFilter} className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded bg-white text-[13px] text-slate-600 hover:bg-slate-100">
                <X size={13} /> Clear
              </button>
            )}
            <span className="text-[11px] text-slate-500">Filters voucher counts; account totals are master counts.</span>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto bg-slate-100 p-3">
        {selectedVoucherType ? (
          <div className="max-w-3xl mx-auto bg-white rounded-lg border border-slate-300 overflow-hidden">
            <div className="bg-slate-200 px-4 py-2 border-b border-slate-300">
              <h2 className="text-[13px] font-bold text-slate-700 uppercase tracking-widest text-center">
                {selectedVoucherType.name} - Month-wise Vouchers
                {financialYear ? ` (${financialYear}-${String(financialYear + 1).slice(-2)})` : ''}
              </h2>
            </div>
            {detailLoading ? (
              <div className="py-16 text-center text-slate-400 text-sm">
                <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue-400" />
                Loading months…
              </div>
            ) : (
              <>
                <div className="divide-y divide-slate-100">
                  {months.map(month => (
                    <button key={`${month.year}-${month.month}`} onClick={() => openMonth(month)}
                      className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-blue-50 group">
                      <span className="text-[13px] font-semibold text-slate-800">{month.label}</span>
                      <span className="flex items-center gap-2">
                        <span className={`text-[13px] tabular-nums ${month.count > 0 ? 'font-bold text-blue-700' : 'text-slate-400'}`}>
                          {month.count}
                        </span>
                        <ChevronRight size={15} className="text-slate-300 group-hover:text-blue-600" />
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-t-2 border-slate-300">
                  <span className="text-[13px] font-bold text-slate-700 uppercase tracking-wide">Total</span>
                  <span className="text-[14px] font-bold tabular-nums text-slate-900">{monthTotal}</span>
                </div>
              </>
            )}
          </div>
        ) : loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">
            <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue-400" />
            Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
              <div className="bg-slate-200 px-4 py-2 border-b border-slate-300">
                <h2 className="text-[13px] font-bold text-slate-700 uppercase tracking-widest text-center">Types of Vouchers</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {voucherTypes.length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-400 text-sm">No voucher types</div>
                ) : voucherTypes.map(v => (
                  <button key={v.id} onClick={() => openVoucherType(v)}
                    className="w-full flex items-center justify-between px-4 py-1.5 text-left hover:bg-blue-50 group">
                    <span className="text-[13px] font-semibold text-slate-800">{v.name}</span>
                    <span className="flex items-center gap-2">
                      <span className={`text-[13px] tabular-nums ${v.count > 0 ? 'font-bold text-blue-700' : 'text-slate-400'}`}>{v.count}</span>
                      <ChevronRight size={14} className="text-slate-300 group-hover:text-blue-600" />
                    </span>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-t-2 border-slate-300">
                <span className="text-[13px] font-bold text-slate-700 uppercase tracking-wide">Total</span>
                <span className="text-[14px] font-bold tabular-nums text-slate-900">{voucherTotal}</span>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-300 overflow-hidden self-start">
              <div className="bg-slate-200 px-4 py-2 border-b border-slate-300">
                <h2 className="text-[13px] font-bold text-slate-700 uppercase tracking-widest text-center">Types of Accounts</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {accounts.length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-400 text-sm">No data</div>
                ) : accounts.map((a, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2 hover:bg-blue-50">
                    <span className="text-[13px] font-semibold text-slate-800">{a.label}</span>
                    <span className={`text-[13px] tabular-nums ${a.count > 0 ? 'font-bold text-blue-700' : 'text-slate-400'}`}>{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
